<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch, type Component } from 'vue';
import { useDebounceFn, useNow } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import {
  PreprocessingTaskStatus,
  TaskStatus,
  VaultFileIntakeStatus,
  VaultFileRevisionSource,
  addContractualPartyToContractFile,
  getVaultAncestors,
  searchVaultFileIntake,
  type ContractManagementDateInDb,
  type ContractTypeRef,
  type UserInDb,
  type VaultFileIntakeItem,
} from '@/api';
import {
  IconArchive,
  IconChevronForward,
  IconEdit,
  IconFolderOpen,
  IconShare,
  IconSign,
  IconUser,
} from '@/components/design/icons';
import WorkspaceLayout from '@/components/design/layout/WorkspaceLayout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterBar from '@/components/design/composite/GaiaFilterBar.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaSortControl, {
  type GaiaSortDirection,
  type GaiaSortOption,
} from '@/components/design/composite/GaiaSortControl.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import GaiaIntakeDetailSplit from '@/components/design/domain/fileVault/GaiaIntakeDetailSplit.vue';
import GaiaIntakeFailureCard from '@/components/design/domain/fileVault/GaiaIntakeFailureCard.vue';
import GaiaIntakeLandingCard from '@/components/design/domain/fileVault/GaiaIntakeLandingCard.vue';
import GaiaIntakeProgressCard, {
  type GaiaIntakeStep,
} from '@/components/design/domain/fileVault/GaiaIntakeProgressCard.vue';
import GaiaIntakeRow from '@/components/design/domain/fileVault/GaiaIntakeRow.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import {
  NO_CONTRACT_TYPE_LABEL,
  preprocessingTaskStatusMap,
  taskStatusMap,
  vaultFileIntakeStatusMap,
  vaultFileRevisionSourceMap,
  type GaiaStatusDescriptor,
} from '@/components/design/statusMaps';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import { isEnumValue } from '@/utils/enumUtils';
import { formatDateForDisplay, formatDateTimeForTable } from '@/utils/datetimeUtils';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import { DATE_TYPE_LABELS, toDateRow } from '@/services/contractManagement/dateRow';
import SelectEntityModal from '@/components/contractManagement/SelectEntityModal.vue';
import EntityFormDialog from '@/views/contractManagement/EntityFormDialog.vue';
import {
  dateConfig,
  deleteEntity,
  entityConfigs,
  type EntityListConfig,
  type EntityRow,
} from '@/views/contractManagement/entityListConfig';
import { entityDetailConfigs } from '@/types/contractManagement/detailConfig';
import FileVaultDeleteModal from '@/components/file_vault/FileVaultDeleteModal.vue';
import ReassignVaultOwnerModal from '@/components/file_vault/ReassignVaultOwnerModal.vue';
import VaultShareModal from '@/components/file_vault/VaultShareModal.vue';
import { vaultActionBlock } from '@/components/file_vault/vaultActionPermissions';
import MoveContractFileModal from '@/components/contractManagement/MoveContractFileModal.vue';
import type { VaultShareTarget } from '@/services/vaultShareService';
import { groupVaultKeys, type VaultUidGroups } from '@/utils/vaultSelection';
import { useAuthStore } from '@/stores/auth';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { useVaultStore } from '@/stores/vaultStore';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { contractRoute, vaultDirectoryRoute } from '@/router/links';
import { logger } from '@/utils/logger';
import { initialsOf } from '@/utils/initials';
import { ACCESS_PERMISSION_WRITE, allows, deniedReason } from '@/utils/accessPermission';

const message = useGaiaToast();

const PAGE_SIZE = 25;
const {
  currentPage,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  columnFilters,
  setColumnFilters,
} = useListQuerySync({
  defaultPageSize: PAGE_SIZE,
  allowedPageSizes: [PAGE_SIZE],
  defaultSortKey: 'created_at',
  // Newest first, so a fresh upload (and the files an archive unpacked into)
  // lands on the first page.
  defaultSortDirection: 'desc',
});

const authStore = useAuthStore();

// With no filter in the URL the page opens on the caller's own uploads.
const ownUid = authStore.backendUser?.uid;
if (ownUid && Object.keys(columnFilters.value).length === 0) {
  setColumnFilters({ created_by_user: [ownUid] });
}

const items = ref<VaultFileIntakeItem[]>([]);
const totalCount = ref(0);
const loading = ref(false);
const loadError = ref<string | null>(null);
const filterOptions = ref<Record<string, unknown[]>>({});
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / PAGE_SIZE)));

// A newer request (typing, paging) can resolve before an older one; only the
// latest may write the list.
let latestRequest = 0;

async function loadIntakes(): Promise<void> {
  const request = ++latestRequest;
  loading.value = true;
  loadError.value = null;
  try {
    const response = await searchVaultFileIntake({
      body: {
        text: searchQuery.value,
        limit: PAGE_SIZE,
        offset: (currentPage.value - 1) * PAGE_SIZE,
        sort_key: sortKey.value,
        sort_direction: sortDirection.value,
        ...filterBody(columnFilters.value),
      },
      throwOnError: true,
    });
    if (request !== latestRequest) return;
    items.value = response.data.results;
    totalCount.value = response.data.result_count;
    filterOptions.value = response.data.filter_options;
  } catch (error) {
    if (request !== latestRequest) return;
    logger.error('Failed to load file intakes:', error);
    loadError.value = 'The files could not be loaded. Please try again.';
  } finally {
    if (request === latestRequest) loading.value = false;
  }
}

function onSearch(term: string): void {
  searchQuery.value = term;
  reload();
}

const SORTS: GaiaSortOption[] = [
  { key: 'created_at', label: 'Created' },
  { key: 'file_name', label: 'File name' },
  { key: 'status', label: 'Status' },
];

function onSortKeyChange(key: string): void {
  sortActivated.value = true;
  sortKey.value = key;
  reload();
}

function onSortDirectionChange(direction: GaiaSortDirection): void {
  sortActivated.value = true;
  sortDirection.value = direction;
  reload();
}

/** A changed query starts again from the first page. */
function reload(): void {
  if (currentPage.value === 1) void loadIntakes();
  else currentPage.value = 1;
}

watch(currentPage, () => void loadIntakes());
watch(columnFilters, reload);
onMounted(() => void loadIntakes());

/* ------------------------------------------------------------------ filters */

// The Contract Management Views recipe: one chip per opened facet, each with
// its value panel. The upload day is always offered; the status, the source,
// the owner, the uploader and the contract's (type, tags) once the endpoint
// reports values for them in `filter_options`.
const FILTER_DEFS = [
  { key: 'status', label: 'Status' },
  { key: 'source', label: 'Source' },
  { key: 'owner_user', label: 'Owner' },
  { key: 'created_by_user', label: 'Uploaded by' },
  { key: 'uploaded', label: 'Upload date' },
  { key: 'type', label: 'Contract type' },
  { key: 'tags', label: 'Tags' },
] as const;
const FILE_FACETS = new Set<string>(['uploaded']);

/**
 * Every facet rides `columnFilters`, so the URL and the chips treat them alike;
 * the status and the file's own go out as the search's dedicated fields, and
 * `uploaded` holds its range as `[from, to]` days (`YYYY-MM-DD`, inclusive, UTC).
 */
function filterBody(filters: Record<string, string[]>): {
  column_filters: Record<string, string[]>;
  statuses?: VaultFileIntakeStatus[];
  sources?: VaultFileRevisionSource[];
  owner_user_uids?: string[];
  created_by_user_uids?: string[];
  uploaded_from?: string;
  uploaded_to?: string;
} {
  const {
    status = [],
    source = [],
    owner_user = [],
    created_by_user = [],
    uploaded = [],
    ...contractFilters
  } = filters;
  return {
    column_filters: contractFilters,
    statuses: status.length
      ? status.filter(value => isEnumValue(VaultFileIntakeStatus, value))
      : undefined,
    sources: source.length
      ? source.filter(value => isEnumValue(VaultFileRevisionSource, value))
      : undefined,
    owner_user_uids: owner_user.length ? owner_user : undefined,
    created_by_user_uids: created_by_user.length ? created_by_user : undefined,
    uploaded_from: uploaded[0] || undefined,
    uploaded_to: uploaded[1] || undefined,
  };
}

const openFilterKeys = ref<string[]>([]);

// A facet picked before the page loaded (a reload, a shared link) opens its
// chip, so the filters the search applies are the ones on screen.
watch(
  columnFilters,
  filters => {
    const picked = Object.keys(filters).filter(
      key => filters[key].length > 0 && !openFilterKeys.value.includes(key)
    );
    if (picked.length) openFilterKeys.value = [...openFilterKeys.value, ...picked];
  },
  { immediate: true }
);

const openChip = ref<string | null>(null);

// Clicking another chip opens it before the open panel's click-outside closes
// itself, so a close only clears the chip it belongs to.
function onChipShow(key: string, show: boolean): void {
  if (show) openChip.value = key;
  else if (openChip.value === key) openChip.value = null;
}

const valueSearch = ref<Record<string, string>>({});

const filterDefs = computed(() =>
  FILTER_DEFS.filter(filter => FILE_FACETS.has(filter.key) || filter.key in filterOptions.value)
);
const availableFilters = computed(() =>
  filterDefs.value.filter(filter => !openFilterKeys.value.includes(filter.key))
);
// A date range counts once, however many ends it has.
const activeFilterCount = computed(() =>
  Object.entries(columnFilters.value).reduce(
    (total, [key, values]) => total + (key === 'uploaded' ? 1 : values.length),
    0
  )
);

function addFilter(key: string): void {
  openFilterKeys.value = [...openFilterKeys.value, key];
  openChip.value = key;
}

function removeFilter(key: string): void {
  openFilterKeys.value = openFilterKeys.value.filter(open => open !== key);
  openChip.value = null;
  setColumnFilters({ ...columnFilters.value, [key]: [] });
}

function toggleFilterValue(key: string, value: string): void {
  const current = columnFilters.value[key] ?? [];
  setColumnFilters({
    ...columnFilters.value,
    [key]: current.includes(value) ? current.filter(v => v !== value) : [...current, value],
  });
}

function filterLabel(key: string): string {
  return FILTER_DEFS.find(filter => filter.key === key)?.label ?? key;
}

/**
 * Status and source values are enum members, so they read through their label
 * maps; a type is its name (`''` for untyped contracts); an owner or uploader
 * is a user uid, named from `filter_options`; tags
 * are prose already.
 */
function valueLabel(key: string, value: string): string {
  if (key === 'status' && isEnumValue(VaultFileIntakeStatus, value)) {
    return vaultFileIntakeStatusMap[value].label;
  }
  if (key === 'type') return value || NO_CONTRACT_TYPE_LABEL;
  if (key === 'source' && isEnumValue(VaultFileRevisionSource, value)) {
    return vaultFileRevisionSourceMap[value];
  }
  if (USER_FACETS.has(key)) return userLabels(key)[value] ?? value;
  return value;
}

/** The values a facet's panel offers: the users that occur, or `filter_options`. */
function facetValues(key: string): unknown[] {
  if (USER_FACETS.has(key)) return Object.keys(userLabels(key));
  return filterOptions.value[key] ?? [];
}

function filterValues(key: string): GaiaFilterValue[] {
  const term = (valueSearch.value[key] ?? '').trim().toLowerCase();
  const picked = columnFilters.value[key] ?? [];
  return facetValues(key)
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .map(value => ({ key: value, label: valueLabel(key, value), checked: picked.includes(value) }))
    .filter(option => !term || option.label.toLowerCase().includes(term))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function pickedLabels(key: string): string[] {
  if (key === 'uploaded') return uploadedRange.value ? [uploadedRangeLabel.value] : [];
  return (columnFilters.value[key] ?? []).map(value => valueLabel(key, value));
}

/*
 * The owner and uploader facets' values: `filter_options` reports the users
 * that occur as `{ uid, email, full_name }`, read as uid → name. The caller is
 * always among them — the page opens filtered to them, uploads or not.
 */
const USER_FACETS = new Set<string>(['owner_user', 'created_by_user']);

function userLabels(key: string): Record<string, string> {
  const users = (filterOptions.value[key] ?? []) as Pick<UserInDb, 'uid' | 'email' | 'full_name'>[];
  const me = authStore.backendUser;
  return Object.fromEntries(
    [...(me ? [me] : []), ...users].map(user => [user.uid, user.full_name || user.email])
  );
}

/** The `uploaded` facet as the date picker's value: `[from, to]` days, or null. */
const uploadedRange = computed<[string, string] | null>(() => {
  const { uploaded = [] } = columnFilters.value;
  const [from = '', to = ''] = uploaded;
  return from || to ? [from, to] : null;
});

// A bare `YYYY-MM-DD` parses as UTC midnight, which reads as the day before
// west of Greenwich; anchoring it to local midnight keeps the picked day.
function dayLabel(day: string): string {
  return formatDateForDisplay(`${day}T00:00:00`);
}

const uploadedRangeLabel = computed(() => {
  const [from, to] = uploadedRange.value ?? ['', ''];
  if (from && to) return from === to ? dayLabel(from) : `${dayLabel(from)} – ${dayLabel(to)}`;
  return from ? `From ${dayLabel(from)}` : `Until ${dayLabel(to)}`;
});

function setUploadedRange(range: [string, string] | null): void {
  setColumnFilters({ ...columnFilters.value, uploaded: range ? [...range] : [] });
}

/* --------------------------------------------------------------------- rail */

const IN_PROGRESS = new Set<VaultFileIntakeStatus>([
  VaultFileIntakeStatus.PREPROCESSING,
  VaultFileIntakeStatus.CONVERTING,
  VaultFileIntakeStatus.EXTRACTING,
]);
const FAILED = new Set<VaultFileIntakeStatus>([
  VaultFileIntakeStatus.FAILED,
  VaultFileIntakeStatus.NOT_CONVERTED,
]);

// The extract-all waits an hour for the OCR (polling every 10 s), then gives up
// for good: a revision still without its party extraction past that won't get one
// on its own.
const STALLED_AFTER_MS = (60 * 60 + 10) * 1000;
const STALLED_HINT =
  "This is taking longer than expected. If you're missing something, please contact our customer support.";
const now = useNow({ interval: 60_000 });

/** Timed from the current revision: a new one can come in long after the file. */
function isStalled(item: VaultFileIntakeItem): boolean {
  const revision = item.vault_file.current_revision;
  if (!revision) return false;
  return (
    IN_PROGRESS.has(item.status) &&
    item.steps.extraction === null &&
    now.value.getTime() - new Date(revision.created_at).getTime() > STALLED_AFTER_MS
  );
}

/** `started_by` is only ever an address, so the initials come from its name part. */
function uploaderInitials(email: string): string {
  return initialsOf(email.split('@')[0]);
}

/** The name lives on the file's current revision, which a file always has once uploaded. */
function fileName(item: VaultFileIntakeItem): string {
  return item.vault_file.current_revision?.file_name ?? '';
}

function userName(user: UserInDb): string {
  return user.full_name || user.email;
}

function rowSub(item: VaultFileIntakeItem): string | undefined {
  return FAILED.has(item.status) ? (item.status_message ?? undefined) : undefined;
}

/**
 * The detail pane's actions — the vault's own actions on the file. Moving is
 * between contracts, so it needs one. An action the caller's bits rule out is
 * left out, by the vault menu's own action → bit map. Delete is not among them:
 * it gets its own affordance.
 */
interface FileAction {
  key: string;
  label: string;
  icon: Component;
}

/**
 * Whose bits decide an action: the folder for showing it, the governing item
 * for share and owner (see governingKey), the file for the rest.
 */
function allowsAction(item: VaultFileIntakeItem, key: string): boolean {
  switch (key) {
    case 'show-in-vault':
      return item.directory !== null && !vaultActionBlock(item.directory, 'open-folder');
    case 'share':
    case 'change-owner':
      return !vaultActionBlock(item.contract ?? item.vault_file, key);
    default:
      return !vaultActionBlock(item.vault_file, key);
  }
}

function fileActions(item: VaultFileIntakeItem): FileAction[] {
  const allowed = (key: string): boolean => allowsAction(item, key);
  return [
    ...(allowed('show-in-vault')
      ? [
          {
            key: 'show-in-vault',
            label: `Show in ${navLabels.fileVault}`,
            // The sidebar's Contract Vault glyph — this opens that page.
            icon: IconArchive,
          },
        ]
      : []),
    ...(item.contract && allowed('move')
      ? [{ key: 'move', label: 'Move to another Contract', icon: IconFolderOpen }]
      : []),
    ...(authStore.userPermissions.can_use_signing && allowed('sign')
      ? [{ key: 'sign', label: 'Sign this', icon: IconSign }]
      : []),
    ...(allowed('share') ? [{ key: 'share', label: 'Share', icon: IconShare }] : []),
    ...(allowed('change-owner')
      ? [{ key: 'change-owner', label: 'Change owner', icon: IconUser }]
      : []),
  ];
}

function onFileAction(item: VaultFileIntakeItem, key: string): void {
  switch (key) {
    case 'show-in-vault':
      if (!item.directory) return;
      void router.push(
        item.directory.uid === vaultRootUid.value
          ? { name: 'file-vault' }
          : vaultDirectoryRoute(item.directory.uid)
      );
      return;
    case 'move':
      void openMove(item);
      return;
    case 'sign':
      void openSign(item);
      return;
    case 'share':
      openShare(item);
      return;
    case 'change-owner':
      openChangeOwner(item);
      return;
    case 'delete':
      deleteKey.value = `file-${item.vault_file.uid}`;
      deleteNames.value = { [deleteKey.value]: fileName(item) };
      showDeleteModal.value = true;
  }
}

/*
 * Share and owner live on the contract once the file is in one — its files
 * have none of their own (see VaultShareTarget) — and on the file until then.
 */
function governingKey(item: VaultFileIntakeItem): string {
  return item.contract ? `contract-${item.contract.uid}` : `file-${item.vault_file.uid}`;
}

const showShareModal = ref(false);
const shareTarget = ref<VaultShareTarget | null>(null);
const shareName = ref('');

function openShare(item: VaultFileIntakeItem): void {
  shareTarget.value = item.contract
    ? { kind: 'contract', uid: item.contract.uid }
    : { kind: 'file', uid: item.vault_file.uid };
  shareName.value = item.contract?.name ?? fileName(item);
  showShareModal.value = true;
}

const showOwnerModal = ref(false);
const ownerGroups = ref<VaultUidGroups>({ fileUids: [], contractUids: [], directoryUids: [] });

function openChangeOwner(item: VaultFileIntakeItem): void {
  ownerGroups.value = groupVaultKeys([governingKey(item)]);
  showOwnerModal.value = true;
}

const showDeleteModal = ref(false);
const deleteKey = ref('');
const deleteNames = ref<Record<string, string>>({});

const showMoveModal = ref(false);
const moveContractFileUid = ref<string | null>(null);

async function openMove(item: VaultFileIntakeItem): Promise<void> {
  try {
    moveContractFileUid.value = await contractFileUidOf(item);
    showMoveModal.value = true;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to open the move dialog.');
  }
}

/** As the contract file's own page: a signing request with just this file. */
async function openSign(item: VaultFileIntakeItem): Promise<void> {
  if (!item.contract) return;
  try {
    const contractFileUid = await contractFileUidOf(item);
    await router.push({
      name: 'signing-contract-request-new',
      params: { uid: item.contract.uid },
      query: { contract_file_uid: contractFileUid },
    });
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to start signing.');
  }
}

const rangeLabel = computed(() => {
  if (totalCount.value === 0) return '0 files';
  const from = (currentPage.value - 1) * PAGE_SIZE + 1;
  const to = from + items.value.length - 1;
  return `${from}–${to} of ${totalCount.value}`;
});

/* -------------------------------------------------------------------- pane */

// `?file=<vault_file_uid>` opens the page on that file — an upload's toast
// links here that way — and the open file stays in the URL for a reload or a
// shared link. A file off the loaded page falls back to the first row.
const route = useRoute();
const router = useRouter();
const selectedUid = ref<string | null>(
  typeof route.query.file === 'string' ? route.query.file : null
);
watch(selectedUid, uid => {
  if (route.name !== 'upload-history') return;
  void router.replace({ query: { ...route.query, file: uid ?? undefined } });
});
const selected = computed<VaultFileIntakeItem | null>(
  () =>
    items.value.find(item => item.vault_file.uid === selectedUid.value) ??
    (items.value.length > 0 ? items.value[0] : null)
);
const selectedIndex = computed(() => (selected.value ? items.value.indexOf(selected.value) : -1));

function step(delta: number): void {
  const index = selectedIndex.value + delta;
  if (index >= 0 && index < items.value.length)
    selectedUid.value = items.value[index].vault_file.uid;
}

const selectedStatus = computed(() =>
  selected.value ? vaultFileIntakeStatusMap[selected.value.status] : null
);
const isInProgress = computed(
  () => selected.value !== null && IN_PROGRESS.has(selected.value.status)
);
const isFailed = computed(() => selected.value !== null && FAILED.has(selected.value.status));
const isSelectedStalled = computed(() => selected.value !== null && isStalled(selected.value));
// The preview is ready well before the file is sorted, so it shows as soon as it is.
const isPreviewReady = computed(
  () => selected.value?.vault_file.current_revision?.preview_status === PreprocessingTaskStatus.DONE
);

function stepOf(label: string, descriptor: GaiaStatusDescriptor | null): GaiaIntakeStep {
  return descriptor
    ? { label, status: descriptor.status, glyph: descriptor.glyph }
    : { label, status: 'pending' };
}

/**
 * Parties and dates are extracted side by side and read as one step: running
 * while either is under way, then the first that didn't finish cleanly — or
 * done once both are.
 */
function extractionStatus(
  parties: TaskStatus | null,
  dates: TaskStatus | undefined
): TaskStatus | null {
  const both = [parties ?? TaskStatus.PENDING, dates ?? TaskStatus.PENDING];
  if (both.includes(TaskStatus.RUNNING)) return TaskStatus.RUNNING;
  if (both.includes(TaskStatus.PENDING)) {
    return both.some(status => status !== TaskStatus.PENDING) ? TaskStatus.RUNNING : null;
  }
  return both.find(status => status !== TaskStatus.DONE) ?? TaskStatus.DONE;
}

const progressSteps = computed<GaiaIntakeStep[]>(() => {
  if (!selected.value) return [];
  const steps = selected.value.steps;
  const extraction = extractionStatus(
    steps.extraction,
    dateExtraction.get(selected.value.vault_file.uid)
  );
  return [
    stepOf(
      'Reading the document',
      steps.preprocessing ? preprocessingTaskStatusMap[steps.preprocessing] : null
    ),
    stepOf('Sorting into your vault', steps.conversion ? taskStatusMap[steps.conversion] : null),
    stepOf('Extracting parties and dates', extraction ? taskStatusMap[extraction] : null),
  ];
});

// Date extraction can outlast the intake status, so the card stays up until it settles.
const showAgentCard = computed(() => isInProgress.value || isExtractingDates.value);

/* ------------------------------------------------------------ live status */

// A file still on its way in reports every step on its vault_file
// subscription; the event isn't an intake row, so re-read the page.
// Debounced: one file emits a burst (preview, signatures, OCR, …).
const refreshIntakes = useDebounceFn(() => void loadIntakes(), 500);

// Matter detection isn't part of the intake status and can settle after the
// file is in its contract, so a file seen processing stays watched until it does.
const awaitingMatter = reactive(new Set<string>());
// Date extraction likewise, and the agent card follows it: a file seen
// processing starts pending, then takes each status its events report.
const dateExtraction = reactive(new Map<string, TaskStatus>());
watch(items, list => {
  for (const item of list) {
    if (IN_PROGRESS.has(item.status)) {
      awaitingMatter.add(item.vault_file.uid);
      if (!dateExtraction.has(item.vault_file.uid)) {
        dateExtraction.set(item.vault_file.uid, TaskStatus.PENDING);
      }
    }
  }
});

function isAwaitingDates(uid: string): boolean {
  const status = dateExtraction.get(uid);
  return status === TaskStatus.PENDING || status === TaskStatus.RUNNING;
}

// A failed or stalled file gets no date events, so its pending status would never settle.
const isExtractingDates = computed(
  () =>
    selected.value !== null &&
    !isFailed.value &&
    !isSelectedStalled.value &&
    isAwaitingDates(selected.value.vault_file.uid)
);

useVaultWebSocket({
  fileUids: () =>
    items.value
      .filter(
        item =>
          IN_PROGRESS.has(item.status) ||
          awaitingMatter.has(item.vault_file.uid) ||
          isAwaitingDates(item.vault_file.uid)
      )
      .map(item => item.vault_file.uid),
  onFileUpdated: () => void refreshIntakes(),
  onFileModelExtractionUpdated: (uid, data) => {
    if (data.task_type === 'contract_management_matter_detection' && data.status !== 'running') {
      awaitingMatter.delete(uid);
    }
    if (data.task_type === 'contract_management_extract_dates') {
      dateExtraction.set(uid, data.status as TaskStatus);
    }
    void refreshIntakes();
  },
});

/* ----------------------------------------------------------- contract edits */

// Every edit below changes the contract, so it needs WRITE on it; without,
// the pane reads as a summary. Null bits (not computed) gate nothing.
const canEditContract = computed(() =>
  allows(selected.value?.contract?.permissions, ACCESS_PERMISSION_WRITE)
);

const draftName = ref('');
watch(
  () => selected.value?.contract?.name,
  name => {
    draftName.value = name ?? '';
  },
  { immediate: true }
);

/** Every row of this contract shows its name and type, so a save patches them all. */
function patchContract(
  contractUid: string,
  patch: Partial<{ name: string; contract_type: ContractTypeRef | null }>
): void {
  for (const item of items.value) {
    if (item.contract?.uid === contractUid) item.contract = { ...item.contract, ...patch };
  }
}

async function saveName(): Promise<void> {
  const contract = selected.value?.contract;
  const name = draftName.value.trim();
  if (!contract || !name || name === contract.name) {
    draftName.value = contract?.name ?? '';
    return;
  }
  try {
    await ContractsService.update(contract.uid, { name });
    patchContract(contract.uid, { name });
  } catch (error) {
    draftName.value = contract.name;
    message.error(error instanceof Error ? error.message : 'Failed to rename the contract.');
  }
}

async function saveType(uid: string | null): Promise<void> {
  const contract = selected.value?.contract;
  if (!contract || uid === (contract.contract_type?.uid ?? null)) return;
  try {
    const updated = await ContractsService.update(contract.uid, { contract_type_uid: uid });
    patchContract(contract.uid, { contract_type: updated.contract_type ?? null });
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to change the contract type.');
  }
}

const showMatterModal = ref(false);

/* --------------------------------------------------------------- vault root */

// The vault root's uid: a file there shows in the vault's own page, and its
// folder reads as the vault rather than the root's internal name.
const vaultStore = useVaultStore();
const contractTypeStore = useContractTypeStore();
const vaultRootUid = ref<string | null>(null);

// The landing card names the file's folder — except the root, whose stored
// name (`organisation_…_root`) is an internal one; there the vault's own name
// stands alone. Resolved once on load; until then the folder reads as is.
onMounted(() => {
  void contractTypeStore.ensureLoaded();
});

onMounted(async () => {
  try {
    vaultRootUid.value = await vaultStore.resolveRootDirectoryUid();
  } catch (error) {
    logger.error('Failed to resolve the vault root:', error);
  }
});

/**
 * The folders above each file's folder, root-first and without the root —
 * the intake item names only the folder itself, the rest comes from
 * `GET /vault/ancestors`. Kept per folder uid, so paging back costs nothing.
 */
const folderTrails = ref<Record<string, string[]>>({});

watch(
  () => selected.value?.directory?.uid,
  async uid => {
    if (!uid || uid in folderTrails.value) return;
    try {
      const [{ data }, rootUid] = await Promise.all([
        getVaultAncestors({ query: { kind: 'directory', uid }, throwOnError: true }),
        vaultStore.resolveRootDirectoryUid(),
      ]);
      vaultRootUid.value = rootUid;
      folderTrails.value = {
        ...folderTrails.value,
        [uid]: data
          .filter(ancestor => ancestor.kind === 'directory' && ancestor.uid !== rootUid)
          .map(ancestor => ancestor.name),
      };
    } catch (error) {
      logger.error('Failed to load the folder trail:', error);
    }
  },
  { immediate: true }
);

function landingLocation(directory: VaultFileIntakeItem['directory']): string | undefined {
  if (!directory) return undefined;
  if (directory.uid === vaultRootUid.value) return navLabels.fileVault;
  return [
    navLabels.fileVault,
    ...(folderTrails.value[directory.uid] ?? []),
    directory.directory_name,
  ].join(' › ');
}

async function assignMatter(matterUid: string): Promise<void> {
  const contract = selected.value?.contract;
  if (!contract) return;
  try {
    await ContractsService.addMatterToContract(matterUid, contract.uid);
    // The modal hands back only the uid; reload for the matter's name.
    await loadIntakes();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to assign the matter.');
  }
}

/* ---------------------------------------------------------- party edits */

// The Contractual Party list's own edit dialog and delete, so a party edited
// here is edited exactly as it is there. Delete removes the party everywhere,
// not just from this file — the same action the list offers.
const partyConfig = entityConfigs['contractual-party'] as EntityListConfig;
const partyFormOpen = ref(false);
const partyFormRow = ref<EntityRow | null>(null);

/**
 * The intake item names the party only; the dialog needs the whole record —
 * which also carries the caller's bits, so a party they can only view is
 * refused here with the reason rather than by the save.
 * ponytail: `VaultFileIntakeParty` has no bits, so the Edit and Delete buttons
 * can't be disabled up front; gate them once the backend adds `permissions`.
 */
async function editParty(partyUid: string): Promise<void> {
  try {
    const entity = await entityDetailConfigs['contractual-party'].getEntityFunction(partyUid);
    const party = 'permissions' in entity ? entity : null;
    const reason = deniedReason(party?.permissions, ACCESS_PERMISSION_WRITE);
    if (reason) {
      message.warning(`${reason} to ${party?.name ?? 'this party'}.`);
      return;
    }
    partyFormRow.value = entity;
    partyFormOpen.value = true;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load the party.');
  }
}

async function deleteParty(partyUid: string): Promise<void> {
  try {
    await deleteEntity(partyConfig, partyUid);
    message.success('Contractual party deleted successfully');
    await loadIntakes();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to delete the party.');
  }
}

/* ----------------------------------------------------------- date edits */

// The contract file page's own date dialog, so a date edited here is edited
// exactly as it is there. Dates are extracted, never created, so only a date
// the file already has can be edited.
const dateFormOpen = ref(false);
const dateFormRow = ref<ContractManagementDateInDb | null>(null);

function editDate(date: ContractManagementDateInDb): void {
  dateFormRow.value = date;
  dateFormOpen.value = true;
}

const showPartyModal = ref(false);
/** The contract file the party picker assigns to — resolved when it opens. */
const partyContractFileUid = ref<string | null>(null);

/**
 * Parties (and a move) hang off the contract file, not the contract, and the
 * intake item carries only the contract — so find this file among the
 * contract's files.
 */
async function contractFileUidOf(item: VaultFileIntakeItem): Promise<string> {
  if (!item.contract) throw new Error('This file isn’t in a contract yet.');
  const files = await ContractFilesService.getAllContractFilesForContract(item.contract.uid);
  const file = files.find(candidate => candidate.vault_file?.uid === item.vault_file.uid);
  if (!file) throw new Error('This file isn’t part of its contract’s files yet.');
  return file.uid;
}

async function openAddParty(): Promise<void> {
  const item = selected.value;
  if (!item?.contract) return;
  try {
    partyContractFileUid.value = await contractFileUidOf(item);
    showPartyModal.value = true;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to open the party picker.');
  }
}

async function assignParty(partyUid: string): Promise<void> {
  if (!partyContractFileUid.value) return;
  try {
    await addContractualPartyToContractFile({
      query: { contractual_party_uid: partyUid, contract_file_uid: partyContractFileUid.value },
      throwOnError: true,
    });
    await loadIntakes();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add the party.');
  }
}

/* --------------------------------------------------------- matter edits */

// The Matter list's own create / edit dialog, so a matter edited here is
// edited exactly as it is there. A matter created here joins the contract.
const matterConfig = entityConfigs.matter as EntityListConfig;
const matterFormOpen = ref(false);
const matterFormRow = ref<EntityRow | null>(null);

/** The intake item names the matter only; the dialog needs the whole record. */
async function editMatter(matterUid: string): Promise<void> {
  try {
    matterFormRow.value = await entityDetailConfigs.matter.getEntityFunction(matterUid);
    matterFormOpen.value = true;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load the matter.');
  }
}

function openCreateMatter(): void {
  matterFormRow.value = null;
  matterFormOpen.value = true;
}

async function onMatterSaved(created?: EntityRow): Promise<void> {
  if (created) await assignMatter(created.uid);
  else await loadIntakes();
}

async function unlinkMatter(matterUid: string): Promise<void> {
  const contract = selected.value?.contract;
  if (!contract) return;
  try {
    await ContractsService.removeMatterFromContract(matterUid, contract.uid);
    for (const item of items.value) {
      if (item.contract?.uid === contract.uid) {
        item.matters = item.matters.filter(matter => matter.uid !== matterUid);
      }
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to unlink the matter.');
  }
}
</script>

<template>
  <workspace-layout :rail-width="420" rail-resizable rail-resize-label="Resize the file list">
    <template #breadcrumb>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'file-vault' }">{{ navLabels.fileVault }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.uploadHistory }}</n-breadcrumb-item>
    </template>

    <template #title>
      <gaia-page-header :title="navLabels.uploadHistory">
        <!-- The way back, mirroring the vault's own "Upload History" button. -->
        <template #actions>
          <router-link v-slot="{ href, navigate }" :to="{ name: 'file-vault' }" custom>
            <gaia-button tag="a" :href="href" size="medium" @click="navigate">
              {{ navLabels.fileVault }}
            </gaia-button>
          </router-link>
        </template>
      </gaia-page-header>
    </template>

    <template #rail>
      <gaia-workspace-pane title="Files" divider="right" hide-scrollbar>
        <template #actions>
          <gaia-sort-control
            :options="SORTS"
            :value="sortKey"
            :direction="sortDirection"
            @update:value="onSortKeyChange"
            @update:direction="onSortDirectionChange"
          />
        </template>

        <template #toolbar>
          <n-flex vertical :size="10">
            <gaia-search-input
              v-model:value="searchQuery"
              variant="panel"
              placeholder="Search files, contracts, parties"
              @search="onSearch"
            />

            <!-- One chip per opened facet, each with its value panel. -->
            <gaia-filter-bar
              :can-add="availableFilters.length > 0"
              :show-reset="activeFilterCount > 0"
              @reset="setColumnFilters({})"
            >
              <gaia-popover
                v-for="key in openFilterKeys"
                :key="key"
                placement="bottom-start"
                :show="openChip === key"
                @update:show="(show: boolean) => onChipShow(key, show)"
              >
                <template #trigger>
                  <gaia-filter-chip
                    :field="filterLabel(key)"
                    :values="pickedLabels(key).slice(0, 2)"
                    :overflow="
                      pickedLabels(key).length > 2 ? `+${pickedLabels(key).length - 2}` : undefined
                    "
                    :active="openChip === key"
                    :wrap="key === 'uploaded'"
                    @remove="removeFilter(key)"
                    @remove-value="
                      (label: string) => {
                        if (key === 'uploaded') return setUploadedRange(null);
                        const value = (columnFilters[key] ?? []).find(
                          picked => valueLabel(key, picked) === label
                        );
                        if (value) toggleFilterValue(key, value);
                      }
                    "
                  />
                </template>
                <!-- The upload day is a range, not a set of values: the
                     calendar itself is the panel. -->
                <gaia-date-picker
                  v-if="key === 'uploaded'"
                  panel
                  type="daterange"
                  value-format="yyyy-MM-dd"
                  :formatted-value="uploadedRange"
                  @update:formatted-value="
                    (range: [string, string] | null) => setUploadedRange(range)
                  "
                  @confirm="openChip = null"
                />
                <gaia-filter-value-panel
                  v-else
                  :values="filterValues(key)"
                  :search="valueSearch[key] ?? ''"
                  :picked-label="
                    (columnFilters[key] ?? []).length
                      ? `${(columnFilters[key] ?? []).length} selected`
                      : undefined
                  "
                  @update:search="(term: string) => (valueSearch[key] = term)"
                  @toggle="(value: string) => toggleFilterValue(key, value)"
                  @clear="setColumnFilters({ ...columnFilters, [key]: [] })"
                />
              </gaia-popover>

              <template #add>
                <gaia-dropdown
                  :options="
                    availableFilters.map(filter => ({ key: filter.key, label: filter.label }))
                  "
                  placement="bottom-start"
                  @select="addFilter"
                >
                  <gaia-add-button subject="filter" variant="dashed" />
                </gaia-dropdown>
              </template>
            </gaia-filter-bar>
          </n-flex>
        </template>

        <gaia-banner v-if="loadError">{{ loadError }}</gaia-banner>

        <gaia-intake-row
          v-for="item in items"
          :key="item.vault_file.uid"
          :name="fileName(item)"
          :sub="rowSub(item)"
          :sub-tone="FAILED.has(item.status) ? 'alert' : 'default'"
          :uploaded-at="formatDateTimeForTable(item.vault_file.created_at)"
          :version="item.vault_file.current_revision?.version_number"
          :uploaded-by="item.started_by ?? undefined"
          :uploaded-by-initials="item.started_by ? uploaderInitials(item.started_by) : ''"
          :status="vaultFileIntakeStatusMap[item.status].status"
          :status-label="vaultFileIntakeStatusMap[item.status].label"
          :selected="selected?.vault_file.uid === item.vault_file.uid"
          @select="selectedUid = item.vault_file.uid"
        />

        <gaia-empty-state
          v-if="!loading && !loadError && items.length === 0"
          :title="searchQuery ? 'No matching files' : 'No files yet'"
        >
          {{
            searchQuery
              ? `Nothing matches “${searchQuery}”.`
              : 'Files you upload into the vault show up here as they are sorted into contracts.'
          }}
        </gaia-empty-state>

        <template #footer>
          <n-flex align="center" justify="space-between" :wrap="false">
            <gaia-text size="nano" tone="subtle">{{ rangeLabel }}</gaia-text>
            <n-flex v-if="totalPages > 1" align="center" :size="6" :wrap="false">
              <gaia-icon-button
                :dimension="26"
                title="Previous page"
                aria-label="Previous page"
                :disabled="currentPage <= 1"
                @click="currentPage -= 1"
              >
                <n-icon :component="IconChevronForward" :style="{ transform: 'rotate(180deg)' }" />
              </gaia-icon-button>
              <gaia-text size="nano" tone="tertiary"
                >{{ currentPage }} / {{ totalPages }}</gaia-text
              >
              <gaia-icon-button
                :dimension="26"
                title="Next page"
                aria-label="Next page"
                :disabled="currentPage >= totalPages"
                @click="currentPage += 1"
              >
                <n-icon :component="IconChevronForward" />
              </gaia-icon-button>
            </n-flex>
          </n-flex>
        </template>
      </gaia-workspace-pane>
    </template>

    <gaia-intake-detail-split
      v-if="selected"
      :show-document="isInProgress || isFailed || !!selected.contract"
    >
      <template #header>
        <!-- Contract actions: a file not yet in a contract has none. -->
        <n-flex align="center" :size="4" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
          <template v-if="selected.contract">
            <gaia-tooltip v-for="action in fileActions(selected)" :key="action.key">
              <template #trigger>
                <gaia-icon-button
                  :dimension="26"
                  :aria-label="action.label"
                  @click="onFileAction(selected, action.key)"
                >
                  <n-icon :component="action.icon" :size="14" />
                </gaia-icon-button>
              </template>
              {{ action.label }}
            </gaia-tooltip>
            <gaia-delete-button
              v-if="allowsAction(selected, 'delete')"
              tooltip
              subject="file"
              @click="onFileAction(selected, 'delete')"
            />
          </template>
        </n-flex>
        <gaia-tag v-if="!selected.contract && selectedStatus">{{ selectedStatus.label }}</gaia-tag>
        <n-flex align="center" :size="8" :wrap="false" :style="{ flex: 'none' }">
          <gaia-icon-button
            :dimension="26"
            title="Previous file"
            aria-label="Previous file"
            :disabled="selectedIndex <= 0"
            @click="step(-1)"
          >
            <n-icon :component="IconChevronForward" :style="{ transform: 'rotate(180deg)' }" />
          </gaia-icon-button>
          <gaia-text size="control" tone="tertiary">
            {{ selectedIndex + 1 }} / {{ items.length }}
          </gaia-text>
          <gaia-icon-button
            :dimension="26"
            title="Next file"
            aria-label="Next file"
            :disabled="selectedIndex >= items.length - 1"
            @click="step(1)"
          >
            <n-icon :component="IconChevronForward" />
          </gaia-icon-button>
        </n-flex>
      </template>

      <template #document>
        <gaia-intake-progress-card
          v-if="showAgentCard && !isPreviewReady"
          :steps="progressSteps"
          :hint="isSelectedStalled ? STALLED_HINT : undefined"
          :prominent-hint="isSelectedStalled"
        />
        <gaia-intake-failure-card v-else-if="isFailed">
          {{ selected.status_message ?? selectedStatus?.label }}
        </gaia-intake-failure-card>
        <gaia-vault-file-pdf-preview v-else :vault-file-uid="selected.vault_file.uid" />
      </template>

      <template #default>
        <gaia-intake-progress-card
          v-if="showAgentCard && isPreviewReady"
          compact
          :steps="progressSteps"
          :hint="isSelectedStalled ? STALLED_HINT : undefined"
          :prominent-hint="isSelectedStalled"
        />
        <template v-if="selected.contract">
          <gaia-intake-landing-card
            :location="landingLocation(selected.directory)"
            :contract-name="selected.contract.name"
            :to="contractRoute(selected.contract.uid)"
          >
            <template #details>
              <n-flex vertical :size="2">
                <span v-if="selected.contract.owner_user">
                  Owner: {{ userName(selected.contract.owner_user) }}
                </span>
                <span>
                  {{ selected.contract.contract_file_count }}
                  {{ selected.contract.contract_file_count === 1 ? 'file' : 'files' }}
                </span>
              </n-flex>
            </template>
          </gaia-intake-landing-card>

          <gaia-form-field label="Contract name" variant="uppercase">
            <!-- A textarea so a long name wraps instead of scrolling out of view;
                 Enter still means "done" rather than a new line. -->
            <gaia-input
              v-if="canEditContract"
              type="textarea"
              :autosize="{ minRows: 1 }"
              :value="draftName"
              :maxlength="255"
              @update:value="(value: string) => (draftName = value)"
              @blur="saveName"
              @keydown.enter.prevent="
                (event: KeyboardEvent) => (event.target as HTMLElement).blur()
              "
            />
            <gaia-text v-else size="row" tone="ink">{{ selected.contract.name }}</gaia-text>
          </gaia-form-field>

          <gaia-form-field label="Contract Type" variant="uppercase">
            <gaia-select
              v-if="canEditContract"
              :value="selected.contract.contract_type?.uid ?? null"
              :options="contractTypeStore.uidOptions"
              placeholder="No type"
              clearable
              filterable
              @update:value="(value: string | null) => saveType(value)"
            />
            <gaia-text v-else size="row" tone="ink">
              {{ selected.contract.contract_type?.name ?? '—' }}
            </gaia-text>
          </gaia-form-field>

          <n-flex :size="24" :wrap="false">
            <gaia-form-field
              :label="DATE_TYPE_LABELS.effective_from"
              variant="uppercase"
              :style="{ flex: 1, minWidth: 0 }"
            >
              <n-flex v-if="selected.effective_from" align="center" :size="2" :wrap="false">
                <gaia-text
                  size="row"
                  :tone="selected.effective_from.date ? 'ink' : 'subtle'"
                  :style="{ flex: 1, minWidth: 0 }"
                >
                  {{
                    selected.effective_from.date
                      ? staticEntityName(selected.effective_from)
                      : 'No date'
                  }}
                </gaia-text>
                <gaia-icon-button
                  v-if="canEditContract"
                  :dimension="28"
                  quaternary
                  title="Edit"
                  aria-label="Edit effective from"
                  @click="editDate(selected.effective_from)"
                >
                  <n-icon :component="IconEdit" :size="14" />
                </gaia-icon-button>
              </n-flex>
              <gaia-status-indicator v-else-if="isExtractingDates" status="running">
                Extracting…
              </gaia-status-indicator>
              <gaia-text v-else size="row" tone="subtle">Not extracted</gaia-text>
            </gaia-form-field>
            <gaia-form-field
              :label="DATE_TYPE_LABELS.effective_to"
              variant="uppercase"
              :style="{ flex: 1, minWidth: 0 }"
            >
              <n-flex v-if="selected.effective_to" align="center" :size="2" :wrap="false">
                <!-- A dateless effective-to means the contract runs indefinitely. -->
                <gaia-text
                  size="row"
                  :tone="selected.effective_to.date ? 'ink' : 'subtle'"
                  :style="{ flex: 1, minWidth: 0 }"
                >
                  {{
                    selected.effective_to.date
                      ? staticEntityName(selected.effective_to)
                      : 'Indefinite'
                  }}
                </gaia-text>
                <gaia-icon-button
                  v-if="canEditContract"
                  :dimension="28"
                  quaternary
                  title="Edit"
                  aria-label="Edit effective to"
                  @click="editDate(selected.effective_to)"
                >
                  <n-icon :component="IconEdit" :size="14" />
                </gaia-icon-button>
              </n-flex>
              <gaia-status-indicator v-else-if="isExtractingDates" status="running">
                Extracting…
              </gaia-status-indicator>
              <gaia-text v-else size="row" tone="subtle">Not extracted</gaia-text>
            </gaia-form-field>
          </n-flex>

          <gaia-form-field :label="navLabels.contractualParties" variant="uppercase">
            <n-flex v-if="selected.parties.length" vertical :size="2">
              <n-flex
                v-for="party in selected.parties"
                :key="party.uid"
                align="center"
                :size="2"
                :wrap="false"
              >
                <gaia-text size="row" tone="ink" :style="{ flex: 1, minWidth: 0 }">
                  {{ party.name }}
                </gaia-text>
                <gaia-icon-button
                  v-if="canEditContract"
                  :dimension="28"
                  quaternary
                  title="Edit"
                  aria-label="Edit party"
                  @click="editParty(party.uid)"
                >
                  <n-icon :component="IconEdit" :size="14" />
                </gaia-icon-button>
                <gaia-popconfirm
                  v-if="canEditContract"
                  :title="`Delete “${party.name}”?`"
                  confirm-label="Delete party"
                  @confirm="deleteParty(party.uid)"
                >
                  <template #trigger>
                    <gaia-delete-button size="md" subject="party" />
                  </template>
                  This deletes the party everywhere, not only from this contract.
                </gaia-popconfirm>
              </n-flex>
            </n-flex>
            <!-- A file reaches its contract only once its parties are extracted,
                 so one still on its way in is still extracting them. -->
            <gaia-status-indicator v-else-if="isInProgress && !isSelectedStalled" status="running">
              Extracting…
            </gaia-status-indicator>
            <gaia-text v-else size="row" tone="subtle">No parties extracted</gaia-text>
            <gaia-add-button
              v-if="canEditContract"
              subject="party"
              variant="dashed"
              size="xs"
              :style="{ alignSelf: 'flex-start', marginTop: '6px' }"
              @click="openAddParty"
            />
          </gaia-form-field>

          <gaia-form-field label="Matters" variant="uppercase">
            <n-flex v-if="selected.matters.length" vertical :size="2">
              <n-flex
                v-for="matter in selected.matters"
                :key="matter.uid"
                align="center"
                :size="2"
                :wrap="false"
              >
                <!-- The row names the matter; its hover tells it apart from a
                     namesake — the type, what it is about, and its tags. -->
                <gaia-tooltip>
                  <template #trigger>
                    <gaia-text size="row" tone="ink" :style="{ flex: 1, minWidth: 0 }">
                      {{ matter.name }}
                    </gaia-text>
                  </template>
                  <n-flex vertical :size="4">
                    <gaia-text weight="semibold">{{ matter.type }}</gaia-text>
                    <span v-if="matter.description">{{ matter.description }}</span>
                    <gaia-text v-if="matter.tags.length" size="meta" tone="subtle">
                      {{ matter.tags.join(' · ') }}
                    </gaia-text>
                  </n-flex>
                </gaia-tooltip>
                <gaia-icon-button
                  v-if="canEditContract"
                  :dimension="28"
                  quaternary
                  title="Edit"
                  aria-label="Edit matter"
                  @click="editMatter(matter.uid)"
                >
                  <n-icon :component="IconEdit" :size="14" />
                </gaia-icon-button>
                <gaia-delete-button
                  v-if="canEditContract"
                  intent="remove"
                  size="md"
                  subject="matter"
                  @click="unlinkMatter(matter.uid)"
                />
              </n-flex>
            </n-flex>
            <n-flex v-if="canEditContract" align="center" :size="6" :style="{ marginTop: '6px' }">
              <gaia-add-button
                subject="matter"
                variant="dashed"
                size="xs"
                @click="showMatterModal = true"
              />
              <gaia-add-button variant="dashed" size="xs" @click="openCreateMatter">
                Create matter
              </gaia-add-button>
            </n-flex>
          </gaia-form-field>
        </template>
      </template>

      <!-- Who and when, pinned under the fields rather than scrolling with them. -->
      <template #footer>
        <n-flex :size="24" :wrap="false">
          <gaia-field-row label="Uploaded" variant="stacked" dense>
            {{ formatDateTimeForTable(selected.vault_file.created_at) }}
          </gaia-field-row>
          <gaia-field-row v-if="selected.started_by" label="Uploaded by" variant="stacked" dense>
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-avatar :size="24">{{ uploaderInitials(selected.started_by) }}</gaia-avatar>
              <gaia-text truncate size="dense" tone="ink">{{ selected.started_by }}</gaia-text>
            </n-flex>
          </gaia-field-row>
        </n-flex>
      </template>
    </gaia-intake-detail-split>

    <entity-form-dialog
      v-model:show="partyFormOpen"
      :config="partyConfig"
      :row="partyFormRow"
      :row-name="partyFormRow ? String(partyFormRow.name ?? '') : ''"
      @saved="loadIntakes"
    />

    <entity-form-dialog
      v-model:show="dateFormOpen"
      :config="dateConfig"
      :row="dateFormRow ? toDateRow(dateFormRow) : null"
      :row-name="dateFormRow ? staticEntityName(dateFormRow) : ''"
      @saved="loadIntakes"
    />

    <entity-form-dialog
      v-model:show="matterFormOpen"
      :config="matterConfig"
      :row="matterFormRow"
      :row-name="matterFormRow ? String(matterFormRow.name ?? '') : ''"
      @saved="onMatterSaved"
    />

    <move-contract-file-modal
      v-if="moveContractFileUid"
      v-model:show="showMoveModal"
      :contract-file-uid="moveContractFileUid"
      @moved="loadIntakes"
    />

    <vault-share-modal v-model:show="showShareModal" :target="shareTarget" :name="shareName" />

    <reassign-vault-owner-modal
      v-model:show="showOwnerModal"
      mode="single"
      :groups="ownerGroups"
      :allow-unassign="authStore.userPermissions.is_org_admin"
      @reassigned="loadIntakes"
    />

    <file-vault-delete-modal
      v-model:show="showDeleteModal"
      :selected-items="deleteKey ? [deleteKey] : []"
      :item-names="deleteNames"
      @deleted="loadIntakes"
    />

    <select-entity-modal
      v-if="partyContractFileUid"
      v-model:show="showPartyModal"
      entity-type="contractual-party"
      :contract-file-uid="partyContractFileUid"
      @assigned="assignParty"
    />

    <select-entity-modal
      v-if="selected?.contract"
      v-model:show="showMatterModal"
      entity-type="matter"
      :contract-uid="selected.contract.uid"
      @assigned="assignMatter"
    />
  </workspace-layout>
</template>
